// src/api/users.js
/**
 * @desc        Users API
 * @version     1.1.0 (Fix for redux store) - 28 July 2025
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { FARMDECK_API } from 'app-config/index.js'
import Axios from 'axios'
import { accountId } from 'helpers/account.js'
import { shallowEqual, useSelector } from 'react-redux'

/** Resolved per call, never at module load: `accountId` reads the page path for a master user, so a constant is frozen to the page open when this module loaded. */
const baseUrl = () => `${FARMDECK_API.replace(':id', accountId)}`
export const cancelSource = Axios.CancelToken.source()

export const fetchUsers = createAsyncThunk(
    'users/fetchUsers',
    async ({ id, force = false, cache = false, tenant } = {}, { getState, rejectWithValue }) => {
        const { lastFetch, data } = getState().users
        const timeSinceLastFetch = Date.now() - lastFetch
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        if (lastFetch === 0 || force || !cache || timeSinceLastFetch >= 5 * 60 * 1000) {
            try {
                const endpoint = id ? `users/${id}` : `users`
                const response = await Axios.get(`${apiURL}${endpoint}`)
                return response.data.items
            } catch (error) {
                return rejectWithValue(error.toString())
            }
        }
        // Return cached items if not forced to fetch, cache is true, and within cache validity
        return data.items
    },
    {
        condition: ({ force, cache } = {}, { getState }) => {
            if (force) return true // Always proceed if force is true
            const { users } = getState()
            // Proceed if cache is false or it's the first fetch or data is stale
            return cache && users.lastFetch !== 0 && shouldFetch(users)
        },
    },
)

export const createUser = createAsyncThunk('user/createUser', async ({ data, tenant }, { rejectWithValue }) => {
    try {
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        const response = await Axios.post(`${apiURL}users/`, data)
        return response.data
    } catch (error) {
        return rejectWithValue(error)
    }
})

export const deleteUser = createAsyncThunk('user/deleteUser', async ({ id, tenant }, { getState, rejectWithValue }) => {
    const originalItems = getState().users.data.items
    try {
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        await Axios.delete(`${apiURL}users/${id}`)
        return id // Return the id of the deleted item
    } catch (error) {
        return rejectWithValue({ error, id, originalItems })
    }
})

export const updateUser = createAsyncThunk('user/updateUser', async ({ id, data, tenant }, { getState, rejectWithValue }) => {
    const originalItems = getState()?.users?.data?.items || []
    try {
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        const response = await Axios.put(`${apiURL}users/${id}`, data)
        return response.data
    } catch (error) {
        rejectWithValue(error.toString())
        return { id, originalItems } // Return original items for potential rollback
    }
})

const initialState = {
    data: {
        items: [],
    },
    loading: false,
    error: null,
    lastFetch: 0,
    lastError: 0,
}

const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchUsers.pending, state => {
                state.loading = true
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                let items = action?.payload ? (Array.isArray(action.payload) ? action.payload : [action.payload]) : []
                state.loading = false
                // state.data.items = items || [] // was causing issues with the redux store
                state.data = {
                    items: items,
                }
                state.lastFetch = Date.now()
                state.error = null
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            .addCase(createUser.fulfilled, (state, action) => {
                state.data.items.push(action.payload)
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                state.data.items = state.data.items.filter(item => item.id !== action.payload)
                state.loading = false
                state.error = null
            })
            .addCase(deleteUser.rejected, (state, action) => {
                if (action.payload?.error) {
                    state.error = action.payload.error.toString()
                    state.lastError = Date.now()
                }
                if (action.payload?.originalItems) {
                    state.data.items = action.payload.originalItems
                }
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                const index = state.data.items.findIndex(item => item.id === action.payload.id)
                if (index >= 0) {
                    state.data.items[index] = { ...state.data.items[index], ...action.payload }
                }
            })
            .addCase(updateUser.rejected, (state, action) => {
                if (action.payload?.error) {
                    state.error = action.payload.error.toString()
                    state.lastError = Date.now()
                }
            })
    },
})

export default usersSlice.reducer

const shouldFetch = state => !state.loading && Date.now() - state.lastFetch > 5 * 60 * 1000

export function useUserMap() {
    return useSelector(state => {
        const items = state?.users?.data?.items || []
        if (!Array.isArray(items)) return {}
        return items.reduce((acc, user) => {
            if (user?.id != null) {
                acc[user.id] = { ...user }
            }
            return acc
        }, {})
    }, shallowEqual)
}
